import React, { useState, useEffect, useRef } from 'react';
import { useMsal } from "@azure/msal-react";
import { loginRequest, graphConfig } from '../authConfig';
import { View, Text, Image, Pressable } from '../lib/react-native-compat';

const UserProfile = () => {
    const { instance, accounts } = useMsal();
    const [userPhoto, setUserPhoto] = useState(null);
    const [userName, setUserName] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (accounts.length > 0) {
            const account = accounts[0];
            setUserName(account.name || '');
            setUserEmail(account.username || '');
            
            // Fetch user profile and photo
            fetchUserData();
        }
    }, [accounts]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown]);

    const fetchUserData = async () => {
        try {
            // Acquire a Microsoft Graph access token. User.Read covers both /me and
            // /me/photo. Request the clean Graph scope first (most reliable), then fall
            // back to the base login scopes if that can't be satisfied silently.
            let response;
            try {
                response = await instance.acquireTokenSilent({
                    scopes: ['User.Read'],
                    account: accounts[0]
                });
            } catch (silentErr) {
                console.warn('UserProfile: User.Read token unavailable silently:', silentErr?.errorCode || silentErr?.message || silentErr);
                response = await instance.acquireTokenSilent({
                    ...loginRequest,
                    account: accounts[0]
                });
            }

            const token = response.accessToken;

            // Profile (/me)
            const profileResponse = await fetch(graphConfig.graphMeEndpoint, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (profileResponse.ok) {
                const profileData = await profileResponse.json();
                if (profileData.displayName) {
                    setUserName(profileData.displayName);
                }
                if (profileData.mail || profileData.userPrincipalName) {
                    setUserEmail(profileData.mail || profileData.userPrincipalName);
                }
            } else {
                console.warn('UserProfile: /me returned HTTP', profileResponse.status);
            }

            // Photo (/me/photo/$value). 404 = no photo set in Azure AD; 403 = the token
            // lacks User.Read consent. Either way we keep the initials fallback.
            const photoResponse = await fetch(graphConfig.graphPhotoEndpoint, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (photoResponse.ok) {
                const blob = await photoResponse.blob();
                setUserPhoto(URL.createObjectURL(blob));
            } else {
                console.warn('UserProfile: /me/photo returned HTTP', photoResponse.status);
            }
        } catch (error) {
            console.error('UserProfile: error fetching user data:', error);
        }
    };

    const handleLogout = () => {
        instance.logoutRedirect({
            postLogoutRedirectUri: "/"
        });
    };

    const getInitials = () => {
        if (!userName) return '?';
        const names = userName.split(' ');
        if (names.length >= 2) {
            return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
        }
        return userName.substring(0, 2).toUpperCase();
    };

    // No MSAL account (local dev bypasses the login gate). In the deployed env users
    // always have one after loginPopup, so render nothing here rather than a button.
    if (accounts.length === 0) {
        return null;
    }

    //console.log('UserProfile rendering with account:', accounts[0]?.username);

    return (
        <View 
            ref={dropdownRef}
            style={{ 
                position: 'relative',
                display: 'flex',
                alignItems: 'center'
            }}
        >
            <Pressable
                onPress={() => setShowDropdown(!showDropdown)}
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    padding: '4px 8px',
                    borderRadius: 20,
                    cursor: 'pointer',
                    background: showDropdown ? '#f5f5f5' : 'transparent',
                    transition: 'background 0.2s'
                }}
            >
                {userPhoto ? (
                    <Image 
                        source={{ uri: userPhoto }}
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: 16,
                            border: '2px solid #e0e0e0'
                        }}
                    />
                ) : (
                    <View
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: 16,
                            background: '#0b142e',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px solid #e0e0e0'
                        }}
                    >
                        <Text style={{ color: '#fff', fontSize: 12, fontWeight: 'bold' }}>
                            {getInitials()}
                        </Text>
                    </View>
                )}
                <Text style={{ fontSize: 14, fontWeight: 500, color: '#333' }}>
                    {userName.split(' ')[0] || 'User'}
                </Text>
                <Text style={{ fontSize: 12, color: '#666' }}>▼</Text>
            </Pressable>

            {showDropdown && (
                <View
                    style={{
                        position: 'absolute',
                        top: 45,
                        right: 0,
                        minWidth: 240,
                        background: '#fff',
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        border: '1px solid #e0e0e0',
                        zIndex: 1000,
                        overflow: 'hidden'
                    }}
                >
                    <View style={{ padding: 16, borderBottom: '1px solid #e0e0e0' }}>
                        <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                            {userPhoto ? (
                                <Image 
                                    source={{ uri: userPhoto }}
                                    style={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 24,
                                        border: '2px solid #e0e0e0'
                                    }}
                                />
                            ) : (
                                <View
                                    style={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 24,
                                        background: '#0b142e',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Text style={{ color: '#fff', fontSize: 18, fontWeight: 'bold' }}>
                                        {getInitials()}
                                    </Text>
                                </View>
                            )}
                            <View style={{ flex: 1 }}>
                                <Text style={{ fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 4 }}>
                                    {userName}
                                </Text>
                                <Text style={{ fontSize: 12, color: '#666' }}>
                                    {userEmail}
                                </Text>
                            </View>
                        </View>
                    </View>

                    <Pressable
                        onPress={handleLogout}
                        style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 8,
                            background: '#fff',
                            transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                    >
                        <Text style={{ fontSize: 16, color: '#d32f2f' }}>↩</Text>
                        <Text style={{ fontSize: 14, color: '#d32f2f', fontWeight: 500 }}>
                            Logout
                        </Text>
                    </Pressable>
                </View>
            )}
        </View>
    );
};

export default UserProfile;
